<script setup lang="ts">
import { useCustomizerStore } from '@/stores/customizer';
import Logo from '/public/images/logos/snapei-b2b-logo.svg';
import LogoLight from '/public/images/logos/snapei-b2b-logo-light.svg';

const customizer = useCustomizerStore();
const isDarkTheme = computed(() => customizer.actTheme === 'DARK_ORANGE_THEME');

const tabs = [
    { title: 'Ruta', icon: 'mdi-map-marker-path', to: '/tat/vendedor/Ruta' },
    { title: 'Clientes', icon: 'mdi-account-group', to: '/tat/vendedor/Clientes' },
    { title: 'Pedidos', icon: 'mdi-clipboard-text', to: '/tat/vendedor/Pedidos' },
];

const route = useRoute();
const activeTab = computed(() => tabs.find((tab) => tab.to === route.path)?.to ?? tabs[0].to);

const tatOrdersStore = useTatOrdersStore();
const tatCartStore = useTatCartStore();

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}
// Pedidos del día = los ya enviados (todayCount, desde el backend) + los
// borradores sin enviar de hoy (allDrafts). Rojo si queda alguno sin subir,
// azul si todo está guardado, sin badge si no hay ninguno.
const draftsToday = computed(
    () => tatCartStore.allDrafts.filter((d) => d.draftDate === todayIso()).length,
);
const todayOrdersTotal = computed(() => tatOrdersStore.todayCount + draftsToday.value);
const pedidosBadgeColor = computed(() => (draftsToday.value > 0 ? 'error' : 'info'));

// El layout no se remonta al navegar entre páginas (solo cambia <NuxtPage>
// adentro), así que se refresca el conteo de enviados en cada cambio de
// ruta — ej. al volver de "Tomar pedido" tras enviar uno.
watch(
    () => route.path,
    () => tatOrdersStore.fetchTodayCount(),
);

// El menú inferior usa el sistema de "layout items" de Vuetify (mismo que
// v-app-bar) para calcular su posición fija — en cargas iniciales en
// viewports angostos (visto en Chrome DevTools modo responsive) ese cálculo
// queda desfasado y el menú no aparece hasta que se dispara un resize.
// DevTools tarda unos instantes en asentar las dimensiones del dispositivo
// emulado, así que un solo resize inmediato puede llegar antes de que el
// tamaño final esté listo — se repite con pequeños delays.
onMounted(() => {
    [0, 150, 500].forEach((delay) => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), delay);
    });
    // Un pedido sin enviar solo se aloja por el día en que se empezó a
    // digitar — se revisa una vez al entrar al área de vendedor.
    tatCartStore.expireStaleDraft();
    tatOrdersStore.fetchTodayCount();
});
</script>

<template>
  <v-locale-provider>
    <v-app class="tat-seller-layout" :theme="customizer.actTheme" :class="[customizer.actTheme]">
      <v-app-bar flat density="comfortable">
        <div class="d-flex align-center flex-grow-1" style="margin-left: 4px">
          <VImg :src="isDarkTheme ? LogoLight : Logo" height="40" width="123" style="flex: none" />
        </div>
        <div class="me-2">
          <LcFullVerticalHeaderThemeToggler />
        </div>
        <LcFullVerticalHeaderProfileDD />
      </v-app-bar>

      <v-main>
        <NuxtPage />
      </v-main>

      <v-bottom-navigation class="tat-bottom-nav" :model-value="activeTab" grow>
        <v-btn v-for="tab in tabs" :key="tab.to" :value="tab.to" :to="tab.to">
          <v-badge
            v-if="tab.to === '/tat/vendedor/Pedidos' && todayOrdersTotal > 0"
            :content="todayOrdersTotal"
            :color="pedidosBadgeColor"
            offset-x="-4"
            offset-y="2"
          >
            <v-icon>{{ tab.icon }}</v-icon>
          </v-badge>
          <v-icon v-else>{{ tab.icon }}</v-icon>
          <span>{{ tab.title }}</span>
        </v-btn>
      </v-bottom-navigation>
    </v-app>
  </v-locale-provider>
</template>

<style scoped>
/* Refuerza la posición fija con CSS puro, independiente del cálculo interno
   de Vuetify (useLayoutItem), que en viewports angostos a veces queda
   desfasado en la carga inicial. */
.tat-bottom-nav {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
}
</style>
